import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NetworkStatus, useLazyQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { useAdmin, ESTABLISHMENTS_STATUS } from "../../../../hooks/useAdmin";
import { useDebouncedSearch } from "../../../../hooks/useDebouncedSearch";
import { useOperation } from "../../../../hooks/useOperation";
import { useListPersons } from "./useListPersons";
import {
  ATTACHMENT_ACTION,
  ENTITY_TYPE,
  USER_ROLE,
  USER_STATUS,
  USER_TYPES,
} from "../../../../common/model/enum";
import { mapPersonToTableUser } from "../utils/mappers";
import { QR_LIST_ESTABLISHMENT } from "../graphql/queries";
import {
  CREATE_USER_MUTATION,
  MT_MANAGE_ATTACHMENT,
  RELAUNCH_USER_MUTATION,
} from "../graphql/mutations";

const initialAddForm = { email: "", role: null, establishment: null };
const initialDraftRole = { establishment: null, role: null };

const buildAuthContext = (accessToken) => ({
  headers: {
    authorization: accessToken ? `Bearer ${accessToken}` : "",
  },
});

const normalizePage = (page) => Math.max(1, Number(page) || 1);

const buildAttachmentDiff = (initialRoles, currentRoles) => {
  const attachments = [];
  const currentMap = new Map(currentRoles.map((role) => [role.entityId, role]));
  const initialMap = new Map(initialRoles.map((role) => [role.entityId, role]));

  currentRoles.forEach((role) => {
    const initialRole = initialMap.get(role.entityId);

    if (!initialRole) {
      attachments.push({
        entityId: role.entityId,
        entityType: ENTITY_TYPE.ESTABLISHMENT,
        role: role.userRole,
        action: ATTACHMENT_ACTION.CREATED,
      });
      return;
    }

    if (initialRole.userRole !== role.userRole) {
      attachments.push({
        entityId: role.entityId,
        entityType: ENTITY_TYPE.ESTABLISHMENT,
        role: role.userRole,
        action: ATTACHMENT_ACTION.EDITED,
      });
    }
  });

  initialRoles.forEach((role) => {
    if (!currentMap.has(role.entityId)) {
      attachments.push({
        entityId: role.entityId,
        entityType: ENTITY_TYPE.ESTABLISHMENT,
        action: ATTACHMENT_ACTION.DELETED,
      });
    }
  });

  return attachments;
};

export const useManageUsers = () => {
  const { t } = useTranslation();
  const { showToast } = useOperation();
  const {
    organization,
    establishmentsPermission,
    accessToken,
    authUserEmail,
    isProAdminForEstablishment,
    assignableEstablishments,
    establishmentsStatus,
    establishmentsError,
  } = useAdmin();

  const [currentPage, setCurrentPageState] = useState(1);
  const [elementsPerPage, setElementsPerPageState] = useState(10);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEstablishment, setSelectedEstablishment] = useState("all");
  const [modalType, setModalType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [addForm, setAddForm] = useState(initialAddForm);
  const [editRoles, setEditRoles] = useState([]);
  const [draftRole, setDraftRole] = useState(initialDraftRole);
  const initialEditRolesRef = useRef([]);

  const canQuery = Boolean(
    organization && (establishmentsPermission || []).length > 0
  );

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(normalizePage(page));
  }, []);

  const paginationParams = useMemo(
    () => ({
      currentPage: Math.max(0, currentPage - 1),
      sizePage: elementsPerPage,
    }),
    [currentPage, elementsPerPage]
  );

  const attachementIds = useMemo(
    () =>
      selectedEstablishment !== "all" ? [selectedEstablishment] : undefined,
    [selectedEstablishment]
  );

  const personStatus = useMemo(() => {
    if (statusFilter === "all") return undefined;
    if (statusFilter === USER_STATUS.MISSING_INFORMATIONS) return USER_STATUS.MISSING_INFORMATIONS;
    if (statusFilter === USER_STATUS.ACTIVATED) return USER_STATUS.ACTIVATED;
    if (statusFilter === USER_STATUS.ACTIVATION_IN_PROGRESS) {
      return USER_STATUS.ACTIVATION_IN_PROGRESS;
    }
    return undefined;
  }, [statusFilter]);

  const queryVariables = useMemo(
    () => ({
      paginationParams,
      search: debouncedSearch || undefined,
      personStatus,
      attachementIds,
    }),
    [paginationParams, debouncedSearch, personStatus, attachementIds]
  );

  useEffect(() => {
    setCurrentPageState(1);
  }, [debouncedSearch, personStatus, attachementIds]);

  const usersQuery = useListPersons(queryVariables, {
    context: buildAuthContext(accessToken),
    skip: !canQuery,
    fetchPolicy: "cache-first",
    nextFetchPolicy: (_, { reason }) =>
      reason === "variables-changed" ? "cache-and-network" : "cache-first",
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  });


  const [fetchUserEstablishments, userEstablishmentsQuery] = useLazyQuery(
    QR_LIST_ESTABLISHMENT,
    {
      fetchPolicy: "no-cache",
    }
  );

  const [createUserMutation] = useMutation(CREATE_USER_MUTATION);
  const [relaunchUserMutation] = useMutation(RELAUNCH_USER_MUTATION);
  const [manageAttachmentMutation] = useMutation(MT_MANAGE_ATTACHMENT);

  const personListData = usersQuery.data?.QR_LIST_PERSON?.data
    || usersQuery.previousData?.QR_LIST_PERSON?.data;

  const users = useMemo(() => {
    const content = personListData?.content || [];
    return content
      .filter((person) => person.email !== authUserEmail)
      .map(mapPersonToTableUser);
  }, [personListData, authUserEmail]);

  const nbPages = personListData?.totalPages || 0;
  const usersLoading =
    usersQuery.networkStatus === NetworkStatus.loading && !personListData;

  const paPermissionIds = useMemo(
    () =>
      new Set(
        (establishmentsPermission || [])
          .filter((permission) => permission.role === USER_ROLE.PA)
          .map((permission) => permission.entityId)
      ),
    [establishmentsPermission]
  );

  const canInviteForEstablishment = useCallback(
    (establishmentId) => {
      if (!establishmentId) return false;
      if (organization?.role === USER_ROLE.PSA) return true;
      return isProAdminForEstablishment(establishmentId);
    },
    [organization?.role, isProAdminForEstablishment]
  );

  const addRoleOptions = useMemo(() => {
    const selectedEstablishmentId = addForm.establishment?.id;
    const canInvite = canInviteForEstablishment(selectedEstablishmentId);

    if (!canInvite) return [];

    return [
      { index: 0, value: USER_ROLE.PA, text: "PRO-ADMIN" },
      { index: 1, value: USER_ROLE.PU, text: "PRO-USER" },
    ];
  }, [addForm.establishment?.id, canInviteForEstablishment]);

  const editRoleOptions = useMemo(
    () => [
      { index: 0, value: USER_ROLE.PA, text: "PRO-ADMIN" },
      { index: 1, value: USER_ROLE.PU, text: "PRO-USER" },
    ],
    []
  );

  const addEstablishmentOptions = useMemo(
    () =>
      assignableEstablishments.map((item, index) => ({
        index,
        value: item.id,
        text: item.company?.legalName,
      })),
    [assignableEstablishments]
  );

  const editEstablishmentOptions = useMemo(() => {
    const usedIds = new Set(editRoles.map((role) => role.entityId));
    return assignableEstablishments
      .filter((item) => !usedIds.has(item.id))
      .map((item, index) => ({
        index,
        value: item.id,
        text: item.company?.legalName,
      }));
  }, [editRoles, assignableEstablishments]);

  const closeModal = useCallback(() => {
    setModalType(null);
    setSelectedUser(null);
    setModalLoading(false);
    setAddForm(initialAddForm);
    setEditRoles([]);
    setDraftRole(initialDraftRole);
    initialEditRolesRef.current = [];
  }, []);

  useEffect(() => {
    if (usersQuery.error || establishmentsError) {
      showToast(t("manageUsers.toast.loadError"), "error");
    }
  }, [usersQuery.error, establishmentsError, showToast, t]);

  useEffect(() => {
    if (userEstablishmentsQuery.error) {
      showToast(t("manageUsers.toast.loadError"), "error");
    }
  }, [userEstablishmentsQuery.error, showToast, t]);

  useEffect(() => {
    const fetchedRoles =
      userEstablishmentsQuery.data?.QR_LIST_ESTABLISHMENT?.data?.content || [];

    if (!fetchedRoles.length) return;

    const allowedRoles = fetchedRoles.filter((establishment) => {
      if (organization?.role === USER_ROLE.PSA) return true;
      return paPermissionIds.has(establishment.id);
    });

    const mappedRoles = allowedRoles.map((item) => ({
      entityId: item.id,
      name: item.company?.legalName,
      userRole: item.userRole || USER_ROLE.PU,
    }));

    initialEditRolesRef.current = mappedRoles;
    setEditRoles(mappedRoles);
  }, [userEstablishmentsQuery.data, paPermissionIds, organization?.role]);

  const refreshUsers = useCallback(async () => {
    await usersQuery.refetch(queryVariables);
  }, [usersQuery, queryVariables]);

  const openAddModal = useCallback(() => {
    setModalType("add");
    setSelectedUser(null);
    setAddForm(initialAddForm);
  }, []);

  const openEditModal = useCallback(
    async (user) => {
      setModalType("edit");
      setSelectedUser(user);
      setEditRoles([]);
      setDraftRole(initialDraftRole);
      initialEditRolesRef.current = [];

      await fetchUserEstablishments({
        variables: {
          paginationParams: { currentPage: 0, sizePage: 1000 },
          organizationId: organization?.entityId,
          userEmail: user?.email,
        },
        context: buildAuthContext(accessToken),
      });
    },
    [fetchUserEstablishments, organization?.entityId, accessToken]
  );

  const openRelaunchModal = useCallback((user) => {
    setSelectedUser(user);
    setModalType("relaunch");
  }, []);

  const onAddFormChange = useCallback(
    (field, value) => {
      setAddForm((previous) => {
        if (field === "establishment") {
          return { ...previous, establishment: value, role: null };
        }
        return { ...previous, [field]: value };
      });
    },
    [setAddForm]
  );

  const onDraftRoleChange = useCallback((field, value) => {
    setDraftRole((previous) => ({ ...previous, [field]: value }));
  }, []);

  const onRoleUpdate = useCallback((entityId, role) => {
    setEditRoles((previous) =>
      previous.map((item) =>
        item.entityId === entityId ? { ...item, userRole: role } : item
      )
    );
  }, []);

  const onRoleRemove = useCallback((entityId) => {
    setEditRoles((previous) =>
      previous.filter((item) => item.entityId !== entityId)
    );
  }, []);

  const onDraftRoleAdd = useCallback(() => {
    if (!draftRole.establishment || !draftRole.role) {
      showToast(t("manageUsers.toast.validationError"), "error");
      return;
    }

    const alreadyExists = editRoles.some(
      (item) => item.entityId === draftRole.establishment.id
    );

    if (alreadyExists) {
      showToast(t("manageUsers.toast.duplicateRole"), "error");
      return;
    }

    setEditRoles((previous) => [
      ...previous,
      {
        entityId: draftRole.establishment.id,
        name: draftRole.establishment.name,
        userRole: draftRole.role.value,
      },
    ]);
    setDraftRole(initialDraftRole);
  }, [draftRole, editRoles, showToast, t]);

  const onSubmitAdd = useCallback(async () => {
    if (!addForm.email || !addForm.role || !addForm.establishment) {
      showToast(t("manageUsers.toast.validationError"), "error");
      return;
    }

    const canInvite = canInviteForEstablishment(addForm.establishment.id);
    const isAllowedRole =
      addForm.role.value === USER_ROLE.PA || addForm.role.value === USER_ROLE.PU;

    if (!canInvite || !isAllowedRole) {
      showToast(t("manageUsers.toast.permissionError"), "error");
      return;
    }

    setModalLoading(true);

    try {
      const userInput = {
        email: addForm.email,
        attributes: {
          applicationRoles: [
            `{"entityId":"${addForm.establishment.id}","role":"${addForm.role.value}","entityType":"ESTABLISHMENT"}`,
          ],
          firstConnection: true,
          owner: false,
          type: USER_TYPES.PRO,
          parentOrganism: organization?.entityId,
        },
        emailVerified: false,
      };

      const response = await createUserMutation({
        variables: { userInput },
        context: buildAuthContext(accessToken),
      });

      const result = response.data?.createUser;
      if (!result || result.error) {
        showToast(t("manageUsers.toast.addError"), "error");
        return;
      }

      await refreshUsers();
      showToast(t("manageUsers.toast.addSuccess"), "success");
      closeModal();
    } catch (error) {
      showToast(t("manageUsers.toast.addError"), "error");
    } finally {
      setModalLoading(false);
    }
  }, [
    addForm,
    organization?.entityId,
    canInviteForEstablishment,
    createUserMutation,
    accessToken,
    refreshUsers,
    showToast,
    closeModal,
    t,
  ]);

  const onSubmitEdit = useCallback(async () => {
    if (!selectedUser?.email || !selectedUser?.id) {
      showToast(t("manageUsers.toast.editError"), "error");
      return;
    }

    const attachments = buildAttachmentDiff(initialEditRolesRef.current, editRoles);
    if (!attachments.length) {
      closeModal();
      return;
    }

    setModalLoading(true);

    try {
      const response = await manageAttachmentMutation({
        variables: {
          email: selectedUser.email,
          personId: selectedUser.id,
          listAttachmentRequest: { attachments },
        },
        context: buildAuthContext(accessToken),
      });

      const result = response.data?.MT_MANAGE_ATTACHMENT;
      if (!result || result.error) {
        showToast(t("manageUsers.toast.editError"), "error");
        return;
      }

      await refreshUsers();
      showToast(t("manageUsers.toast.editSuccess"), "success");
      closeModal();
    } catch (error) {
      showToast(t("manageUsers.toast.editError"), "error");
    } finally {
      setModalLoading(false);
    }
  }, [
    selectedUser,
    editRoles,
    manageAttachmentMutation,
    accessToken,
    refreshUsers,
    showToast,
    closeModal,
    t,
  ]);

  const onSubmitRelaunch = useCallback(async () => {
    if (!selectedUser?.email) {
      showToast(t("manageUsers.toast.relaunchError"), "error");
      return;
    }

    setModalLoading(true);

    try {
      const response = await relaunchUserMutation({
        variables: { userEmail: selectedUser.email },
        context: buildAuthContext(accessToken),
      });

      const result = response.data?.relaunchUser;
      if (!result || result.error) {
        showToast(t("manageUsers.toast.relaunchError"), "error");
        return;
      }

      await refreshUsers();
      showToast(t("manageUsers.toast.relaunchSuccess"), "success");
      closeModal();
    } catch (error) {
      showToast(t("manageUsers.toast.relaunchError"), "error");
    } finally {
      setModalLoading(false);
    }
  }, [
    selectedUser,
    relaunchUserMutation,
    accessToken,
    refreshUsers,
    showToast,
    closeModal,
    t,
  ]);

  return {
    users,
    usersLoading,
    currentPage,
    setCurrentPage,
    elementsPerPage,
    nbPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedEstablishment,
    setSelectedEstablishment,
    isAddModalOpen: modalType === "add",
    isEditModalOpen: modalType === "edit",
    isRelaunchModalOpen: modalType === "relaunch",
    selectedUser,
    addForm,
    addRoleOptions,
    addEstablishmentOptions,
    draftRole,
    editRoles,
    editRoleOptions,
    editEstablishmentOptions,
    modalLoading:
      modalLoading ||
      userEstablishmentsQuery.loading ||
      establishmentsStatus === ESTABLISHMENTS_STATUS.LOADING,
    openAddModal,
    openEditModal,
    openRelaunchModal,
    closeModal,
    onAddFormChange,
    onDraftRoleChange,
    onRoleUpdate,
    onRoleRemove,
    onDraftRoleAdd,
    onSubmitAdd,
    onSubmitEdit,
    onSubmitRelaunch,
  };
};
